import { getUser, getToken } from "../utils/authService";
import { useState, useEffect } from "react";
import { Wrench, Plus, Calendar, AlertTriangle, CheckCircle, Clock, DollarSign, Truck, Download, Filter, Search } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const EquipmentMaintenance = () => {
  const [schedules, setSchedules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("schedules");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Schedule Dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    equipment_id: "",
    maintenance_type: "Routine Service",
    frequency_days: 30,
    last_performed: "",
    next_due_date: "",
    estimated_cost: "",
    notes: ""
  });

  // Log Dialog
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [logForm, setLogForm] = useState({
    schedule_id: "",
    equipment_id: "",
    service_date: new Date().toISOString().split('T')[0],
    maintenance_type: "",
    description: "",
    parts_replaced: "",
    labor_hours: "",
    parts_cost: "",
    labor_cost: "",
    vendor_name: "",
    invoice_number: "",
    next_service_notes: ""
  });

  const MAINTENANCE_TYPES = [
    "Routine Service",
    "Oil Change",
    "Filter Replacement",
    "Brake Service",
    "Tire Replacement",
    "Engine Overhaul",
    "Hydraulic Service",
    "Electrical Repair",
    "Body Repair",
    "Annual Inspection",
    "Other"
  ];

  const currentUser = getUser() || {};
  const canManage = currentUser.role === "Master Admin" || currentUser.role === "Management";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [schedulesRes, logsRes, equipRes, statsRes] = await Promise.all([
        apiClient.get('/maintenance/schedules'),
        apiClient.get('/maintenance/logs'),
        apiClient.get('/equipment/all'),
        apiClient.get('/maintenance/stats')
      ]);
      setSchedules(schedulesRes.data);
      setLogs(logsRes.data);
      setEquipment(equipRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch maintenance data");
    } finally {
      setLoading(false);
    }
  };

  const getEquipmentName = (equipmentId) => {
    const eq = equipment.find(e => e.id === equipmentId);
    return eq ? `${eq.name} (${eq.equipment_id})` : equipmentId;
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      equipment_id: "",
      maintenance_type: "Routine Service",
      frequency_days: 30,
      last_performed: "",
      next_due_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      estimated_cost: "",
      notes: ""
    });
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/maintenance/schedules', {
        ...scheduleForm,
        frequency_days: parseInt(scheduleForm.frequency_days),
        estimated_cost: parseFloat(scheduleForm.estimated_cost) || 0
      });
      toast.success("Maintenance schedule created");
      setScheduleDialogOpen(false);
      resetScheduleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create schedule");
    }
  };

  const openLogDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setLogForm({
      schedule_id: schedule.id,
      equipment_id: schedule.equipment_id,
      service_date: new Date().toISOString().split('T')[0],
      maintenance_type: schedule.maintenance_type,
      description: "",
      parts_replaced: "",
      labor_hours: "",
      parts_cost: "",
      labor_cost: "",
      vendor_name: "",
      invoice_number: "",
      next_service_notes: ""
    });
    setLogDialogOpen(true);
  };

  const handleLogSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/maintenance/logs', {
        ...logForm,
        labor_hours: parseFloat(logForm.labor_hours) || 0,
        parts_cost: parseFloat(logForm.parts_cost) || 0,
        labor_cost: parseFloat(logForm.labor_cost) || 0
      });
      toast.success("Maintenance log recorded");
      setLogDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record maintenance");
    }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) return;
    try {
      await apiClient.delete(`/maintenance/schedules/${scheduleId}`);
      toast.success("Schedule deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  const getDueStatus = (nextDueDate) => {
    const today = new Date();
    const dueDate = new Date(nextDueDate);
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { status: "Overdue", color: "bg-error/20 text-error", icon: AlertTriangle };
    if (diffDays <= 7) return { status: "Due Soon", color: "bg-warning/20 text-warning", icon: Clock };
    return { status: "Scheduled", color: "bg-success/20 text-success", icon: CheckCircle };
  };

  const filteredSchedules = schedules.filter(s => {
    const matchesFilter = filterStatus === "all" || 
      (filterStatus === "overdue" && getDueStatus(s.next_due_date).status === "Overdue") ||
      (filterStatus === "due_soon" && getDueStatus(s.next_due_date).status === "Due Soon") ||
      (filterStatus === "scheduled" && getDueStatus(s.next_due_date).status === "Scheduled");
    
    const matchesSearch = searchTerm === "" || 
      getEquipmentName(s.equipment_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.maintenance_type.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const downloadReport = async () => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/maintenance`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'maintenance_report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Report downloaded successfully");
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  const totalLogCost = logForm.parts_cost && logForm.labor_cost 
    ? (parseFloat(logForm.parts_cost) || 0) + (parseFloat(logForm.labor_cost) || 0) 
    : 0;

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">EQUIPMENT MAINTENANCE</h1>
          <p className="text-slate-600 mt-2 font-body">Manage maintenance schedules and service records</p>
        </div>
        <button onClick={downloadReport} className="btn-outline flex items-center space-x-2">
          <Download className="w-4 h-4" />
          <span>Download Report</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Active Schedules</p>
          <p className="font-heading font-black text-3xl text-primary font-mono">{stats?.total_schedules || 0}</p>
        </div>
        <div className="stat-card border-l-4 border-l-error">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Overdue</p>
          <p className="font-heading font-black text-3xl text-error font-mono">{stats?.overdue || 0}</p>
        </div>
        <div className="stat-card border-l-4 border-l-warning">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Due in 7 Days</p>
          <p className="font-heading font-black text-3xl text-warning font-mono">{stats?.due_soon || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">This Year Cost</p>
          <p className="font-heading font-black text-2xl text-primary font-mono">₹{(stats?.total_cost_this_year || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        {[
          { id: "schedules", label: "Schedules", icon: Calendar, count: schedules.length },
          { id: "logs", label: "Service Logs", icon: Wrench, count: logs.length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 px-4 font-medium text-sm transition-colors flex items-center space-x-2 ${
              activeTab === tab.id ? "border-b-2 border-primary text-primary" : "text-slate-600 hover:text-primary"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label} ({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Schedules Tab */}
      {activeTab === "schedules" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search equipment or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary w-64"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Schedules</option>
                  <option value="overdue">Overdue</option>
                  <option value="due_soon">Due Soon (7 days)</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>
            </div>
            {canManage && (
              <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
                <DialogTrigger asChild>
                  <button onClick={resetScheduleForm} className="btn-primary flex items-center space-x-2">
                    <Plus className="w-4 h-4" />
                    <span>Add Schedule</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">ADD MAINTENANCE SCHEDULE</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleScheduleSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment *</label>
                        <select
                          required
                          value={scheduleForm.equipment_id}
                          onChange={(e) => setScheduleForm({...scheduleForm, equipment_id: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select Equipment</option>
                          {equipment.map(e => (
                            <option key={e.id} value={e.id}>{e.name} ({e.equipment_id})</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Maintenance Type *</label>
                        <select
                          required
                          value={scheduleForm.maintenance_type}
                          onChange={(e) => setScheduleForm({...scheduleForm, maintenance_type: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {MAINTENANCE_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Frequency (Days) *</label>
                        <input
                          type="number"
                          required
                          min="1"
                          value={scheduleForm.frequency_days}
                          onChange={(e) => setScheduleForm({...scheduleForm, frequency_days: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Last Performed</label>
                        <input
                          type="date"
                          value={scheduleForm.last_performed}
                          onChange={(e) => setScheduleForm({...scheduleForm, last_performed: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Next Due Date *</label>
                        <input
                          type="date"
                          required
                          value={scheduleForm.next_due_date}
                          onChange={(e) => setScheduleForm({...scheduleForm, next_due_date: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Estimated Cost (₹)</label>
                      <input
                        type="number"
                        value={scheduleForm.estimated_cost}
                        onChange={(e) => setScheduleForm({...scheduleForm, estimated_cost: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Notes</label>
                      <textarea
                        value={scheduleForm.notes}
                        onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        rows={3}
                        placeholder="Any additional notes..."
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <button type="button" onClick={() => setScheduleDialogOpen(false)} className="btn-outline">Cancel</button>
                      <button type="submit" className="btn-primary">Add Schedule</button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Equipment</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Maintenance Type</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Frequency</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Last Done</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Next Due</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Est. Cost</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchedules.map(schedule => {
                  const due = getDueStatus(schedule.next_due_date);
                  const DueIcon = due.icon;
                  return (
                    <tr key={schedule.id} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <Truck className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-sm">{getEquipmentName(schedule.equipment_id)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">{schedule.maintenance_type}</td>
                      <td className="py-3 px-4 text-center font-mono text-sm">{schedule.frequency_days} days</td>
                      <td className="py-3 px-4 text-center font-mono text-sm">{schedule.last_performed || '-'}</td>
                      <td className="py-3 px-4 text-center font-mono text-sm font-medium">{schedule.next_due_date}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm">₹{(schedule.estimated_cost || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium ${due.color}`}>
                          <DueIcon className="w-3 h-3" />
                          <span>{due.status}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => openLogDialog(schedule)}
                            className="text-xs text-primary hover:underline"
                          >
                            Record Service
                          </button>
                          {canManage && (
                            <button
                              onClick={() => deleteSchedule(schedule.id)}
                              className="text-xs text-error hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredSchedules.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-xl mb-2">No Maintenance Schedules</h3>
                <p className="text-slate-600 mb-4">Add equipment maintenance schedules to track service intervals.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service Logs Tab */}
      {activeTab === "logs" && (
        <div className="stat-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Equipment</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Type</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Description</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Parts Cost</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Labor Cost</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Total</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Vendor</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="py-3 px-4 font-mono text-sm">{log.service_date}</td>
                  <td className="py-3 px-4 text-sm font-medium">{getEquipmentName(log.equipment_id)}</td>
                  <td className="py-3 px-4 text-sm">{log.maintenance_type}</td>
                  <td className="py-3 px-4 text-sm max-w-xs truncate">{log.description || '-'}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">₹{(log.parts_cost || 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">₹{(log.labor_cost || 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-primary">₹{((log.parts_cost || 0) + (log.labor_cost || 0)).toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm">{log.vendor_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Wrench className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="font-heading font-bold text-xl mb-2">No Service Records</h3>
              <p className="text-slate-600">Service records will appear here when maintenance is performed.</p>
            </div>
          )}
        </div>
      )}

      {/* Log Dialog */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">RECORD MAINTENANCE</DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <form onSubmit={handleLogSubmit} className="space-y-4 mt-4">
              <div className="bg-slate-50 p-3 rounded-sm">
                <p className="text-sm"><strong>Equipment:</strong> {getEquipmentName(selectedSchedule.equipment_id)}</p>
                <p className="text-sm"><strong>Maintenance Type:</strong> {selectedSchedule.maintenance_type}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Service Date *</label>
                  <input
                    type="date"
                    required
                    value={logForm.service_date}
                    onChange={(e) => setLogForm({...logForm, service_date: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Labor Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    value={logForm.labor_hours}
                    onChange={(e) => setLogForm({...logForm, labor_hours: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Work Description *</label>
                <textarea
                  required
                  value={logForm.description}
                  onChange={(e) => setLogForm({...logForm, description: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  rows={2}
                  placeholder="Describe the maintenance work performed..."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Parts Replaced</label>
                <input
                  type="text"
                  value={logForm.parts_replaced}
                  onChange={(e) => setLogForm({...logForm, parts_replaced: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  placeholder="List parts replaced (comma separated)"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Parts Cost (₹)</label>
                  <input
                    type="number"
                    value={logForm.parts_cost}
                    onChange={(e) => setLogForm({...logForm, parts_cost: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Labor Cost (₹)</label>
                  <input
                    type="number"
                    value={logForm.labor_cost}
                    onChange={(e) => setLogForm({...logForm, labor_cost: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Total Cost</label>
                  <div className="px-4 py-2 bg-slate-100 rounded-sm font-mono font-semibold text-primary">
                    ₹{totalLogCost.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Vendor Name</label>
                  <input
                    type="text"
                    value={logForm.vendor_name}
                    onChange={(e) => setLogForm({...logForm, vendor_name: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                    placeholder="Service provider name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Invoice Number</label>
                  <input
                    type="text"
                    value={logForm.invoice_number}
                    onChange={(e) => setLogForm({...logForm, invoice_number: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono"
                    placeholder="INV-XXXX"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setLogDialogOpen(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary">Record Maintenance</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentMaintenance;
